import AsyncStorage from '@react-native-async-storage/async-storage'
import type { User } from '@pff-consumer/schema'
import { getUniqueDeviceIdFromStorage } from '@pff-consumer/utils'
import { useEffect, useState } from 'react'
import { debounce } from 'lodash'

import { useUpsertDeviceMutation } from './account-api'

const saveTokenToStorage = async (user: User, token: string, isPremium: boolean, isExpired: boolean) => {
  if (token) {
    try {
      await AsyncStorage.setItem('@user_token', token)
      await AsyncStorage.setItem('@user', JSON.stringify({ ...user, isPremium, isExpired }))
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log('Could not save token', e)
    }
  }
}

const updateTokenInStorage = async (token: string) => {
  try {
    await AsyncStorage.setItem('@user_token', token)
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('Could not save token', e)
  }
}

const getTokenFromStorage = async (): Promise<string | null> => {
  try {
    const tok = await AsyncStorage.getItem('@user_token')
    if (tok) {
      return tok
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('Could get token', e)
    return null
  }

  return null
}

const isUser = (data: unknown): data is User => {
  if (typeof data === 'object' && data !== null) {
    const maybeUser: User = data as User
    if (typeof maybeUser.uid === 'string') return true
  }
  return false
}

export const getUserFromStorage = async (): Promise<User | null> => {
  try {
    const userString = await AsyncStorage.getItem('@user')
    if (userString) {
      const userData: unknown = JSON.parse(userString)

      return isUser(userData) ? userData : null
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('Could user', e)
    return null
  }

  return null
}

const removeTokenFromStorage = async () => {
  try {
    await AsyncStorage.removeItem('@user_token')
    await AsyncStorage.removeItem('@user')
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('Could delete token', e)
  }
}

export const useAccountStorage = () => {
  const [user, setUser] = useState<User>()
  const [token, setToken] = useState<string>()
  const [uniqueDeviceId, setUniqueDeviceId] = useState<string>()
  const [isReady, setIsReady] = useState<boolean>(false)

  useEffect(() => {
    const init = async () => {
      const storedUser = await getUserFromStorage()
      const storedToken = await getTokenFromStorage()
      const storedUniqueDeviceId = await getUniqueDeviceIdFromStorage()

      if (storedUser && storedToken) {
        setUser(storedUser)
        setToken(storedToken)
      }
      if (storedUniqueDeviceId) {
        setUniqueDeviceId(storedUniqueDeviceId)
      }

      setIsReady(true)
    }
    init()
  }, [])

  return {
    getTokenFromStorage,
    getUserFromStorage,
    isReady,
    removeTokenFromStorage,
    saveTokenToStorage,
    updateTokenInStorage,
    token,
    uniqueDeviceId,
    user,
  }
}

const upsertDeviceDebounced = debounce(async (upsertDevice: () => any) => {
  const deviceSavedStatus = await AsyncStorage.getItem('@device_id_persisted')

  if (deviceSavedStatus !== 'true') {
    const savedDevice: any = await upsertDevice()

    if (savedDevice?.data?.userId) {
      await AsyncStorage.setItem('@device_id_persisted', 'true')
    }
  }
}, 3000)

export const usePersistDeviceToBackend = () => {
  const [upsertDeviceMutation, { isLoading }] = useUpsertDeviceMutation()

  async function upsertDevice({ deviceId, systemName }: { deviceId: string; systemName: string }) {
    if (!isLoading) {
      upsertDeviceDebounced(() => upsertDeviceMutation({ deviceId, systemName }))
    }
  }

  return {
    upsertDevice,
  }
}
